import { useState, useEffect, useCallback, useRef } from 'react';
import { CategoryData, GlossaryEntry, WeightCluster } from '@/lib/api';

const CLUSTER_FILE_MAP: Record<string, string> = {
    'ENVIRONMENT': 'environment',
    'DESIGN': 'design',
    'ECONOMIC': 'economic',
    'SOCIAL': 'social',
};

// Global cache for static data to persist across component remounts (language switches)
const globalDataCache: Record<string, {
    categoryData: CategoryData[];
    allCategoryData: Record<string, CategoryData[]>;
    weightsData: WeightCluster | null;
    allWeightsData: WeightCluster[];
    glossary: GlossaryEntry[];
    enNameMap: Map<string, { enCategory: string; enSubcategory: string }>;
}> = {};

const CACHE_INVALIDATED_EVENT = 'scoring-cache-invalidated';

/** Clear all cached cluster data so the next render re-fetches from the API. */
export function invalidateScoringDataCache() {
    for (const key of Object.keys(globalDataCache)) {
        delete globalDataCache[key];
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CACHE_INVALIDATED_EVENT));
    }
}

export function useCategoryScoringData(cluster: 'ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL', locale: string) {
    const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
    const [allCategoryData, setAllCategoryData] = useState<Record<string, CategoryData[]>>({});
    const [weightsData, setWeightsData] = useState<WeightCluster | null>(null);
    const [allWeightsData, setAllWeightsData] = useState<WeightCluster[]>([]);
    const [glossary, setGlossary] = useState<GlossaryEntry[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [enNameMap, setEnNameMap] = useState<Map<string, { enCategory: string; enSubcategory: string }>>(new Map());
    const [loading, setLoading] = useState(true);
    const [cacheVersion, setCacheVersion] = useState(0);

    // Re-fetch when admin invalidates the cache (even if this component stays mounted)
    useEffect(() => {
        const handler = () => setCacheVersion(v => v + 1);
        window.addEventListener(CACHE_INVALIDATED_EVENT, handler);
        return () => window.removeEventListener(CACHE_INVALIDATED_EVENT, handler);
    }, []);

    // Build mapping from locale names to EN names
    const buildEnNameMap = useCallback((localeData: CategoryData[], enData: CategoryData[]): Map<string, { enCategory: string; enSubcategory: string }> => {
        const map = new Map<string, { enCategory: string; enSubcategory: string }>();

        // Match categories by index (they should be in same order in both files)
        for (let i = 0; i < localeData.length && i < enData.length; i++) {
            const localeCat = localeData[i];
            const enCat = enData[i];

            // Match subcategories by index within each category
            for (let j = 0; j < localeCat.subcategories.length && j < enCat.subcategories.length; j++) {
                const localeSub = localeCat.subcategories[j];
                const enSub = enCat.subcategories[j];

                const localeKey = `${localeCat.category}::${localeSub.name}`;
                map.set(localeKey, {
                    enCategory: enCat.category,
                    enSubcategory: enSub.name,
                });
            }
        }

        return map;
    }, []);

    // Load JSON data
    useEffect(() => {
        const loadData = async () => {
            const cacheKey = `${cluster}-${locale}`;
            const isCached = !!globalDataCache[cacheKey];

            if (!isCached) {
                setLoading(true);
            }

            try {
                if (isCached) {
                    const cached = globalDataCache[cacheKey];
                    setCategoryData(cached.categoryData);
                    setAllCategoryData(cached.allCategoryData);
                    setWeightsData(cached.weightsData);
                    setAllWeightsData(cached.allWeightsData);
                    setGlossary(cached.glossary);
                    setEnNameMap(cached.enNameMap);
                    // Reset expansion to all open on reload/locale change
                    setExpandedCategories(new Set(cached.categoryData.map(c => c.category)));
                    setLoading(false);
                } else {
                    const filePrefix = CLUSTER_FILE_MAP[cluster];
                    const suffix = locale === 'pt' ? '_pt' : '_en';

                    // Load ALL cluster category data, weights, and glossary in parallel
                    // Also load EN versions for name mapping (needed for DB lookups)
                    const [
                        catResponse, envResponse, designResponse, econResponse, socialResponse,
                        catEnResponse, envEnResponse, designEnResponse, econEnResponse, socialEnResponse,
                        weightsResponse, glossaryResponse
                    ] = await Promise.all([
                        // Locale versions (for display)
                        fetch(`/api/scores-data/${filePrefix}?lang=${locale === 'pt' ? 'pt' : 'en'}`),
                        fetch(`/api/scores-data/environment?lang=${locale === 'pt' ? 'pt' : 'en'}`),
                        fetch(`/api/scores-data/design?lang=${locale === 'pt' ? 'pt' : 'en'}`),
                        fetch(`/api/scores-data/economic?lang=${locale === 'pt' ? 'pt' : 'en'}`),
                        fetch(`/api/scores-data/social?lang=${locale === 'pt' ? 'pt' : 'en'}`),
                        // EN versions (for DB key mapping)
                        fetch(`/api/scores-data/${filePrefix}?lang=en`),
                        fetch(`/api/scores-data/environment?lang=en`),
                        fetch(`/api/scores-data/design?lang=en`),
                        fetch(`/api/scores-data/economic?lang=en`),
                        fetch(`/api/scores-data/social?lang=en`),
                        // Weights (always EN - cluster names match code constants)
                        fetch(`/api/weights-data?lang=en`),
                        // Glossary (locale-specific for display)
                        fetch(`/api/glossary?lang=${locale === 'pt' ? 'pt' : 'en'}`),
                    ]);

                    // Parse locale data
                    const catData: CategoryData[] = await catResponse.json();
                    const envData: CategoryData[] = await envResponse.json();
                    const designData: CategoryData[] = await designResponse.json();
                    const econData: CategoryData[] = await econResponse.json();
                    const socialData: CategoryData[] = await socialResponse.json();

                    // Parse EN data for name mapping
                    const catEnData: CategoryData[] = await catEnResponse.json();
                    const envEnData: CategoryData[] = await envEnResponse.json();
                    const designEnData: CategoryData[] = await designEnResponse.json();
                    const econEnData: CategoryData[] = await econEnResponse.json();
                    const socialEnData: CategoryData[] = await socialEnResponse.json();

                    const allWeights: WeightCluster[] = await weightsResponse.json();
                    const glossaryData: GlossaryEntry[] = await glossaryResponse.json();

                    // Build locale-to-EN name mapping for ALL clusters (needed for overall score calculation)
                    const nameMap = new Map<string, { enCategory: string; enSubcategory: string }>();

                    // Add mappings for current cluster
                    for (const [key, value] of buildEnNameMap(catData, catEnData).entries()) {
                        nameMap.set(key, value);
                    }
                    // Add mappings for all other clusters
                    for (const [key, value] of buildEnNameMap(envData, envEnData).entries()) {
                        nameMap.set(key, value);
                    }
                    for (const [key, value] of buildEnNameMap(designData, designEnData).entries()) {
                        nameMap.set(key, value);
                    }
                    for (const [key, value] of buildEnNameMap(econData, econEnData).entries()) {
                        nameMap.set(key, value);
                    }
                    for (const [key, value] of buildEnNameMap(socialData, socialEnData).entries()) {
                        nameMap.set(key, value);
                    }

                    // Cache the results
                    const cachedData = {
                        categoryData: catData,
                        allCategoryData: {
                            'ENVIRONMENT': envData,
                            'DESIGN': designData,
                            'ECONOMIC': econData,
                            'SOCIAL': socialData,
                        },
                        weightsData: allWeights.find(w => w.cluster === cluster) || null,
                        allWeightsData: allWeights,
                        glossary: glossaryData,
                        enNameMap: nameMap
                    };
                    globalDataCache[cacheKey] = cachedData;

                    setEnNameMap(nameMap);
                    setCategoryData(catData);
                    setAllCategoryData(cachedData.allCategoryData);
                    setWeightsData(cachedData.weightsData);
                    setAllWeightsData(allWeights);
                    setGlossary(glossaryData);
                    setExpandedCategories(new Set(catData.map(c => c.category)));
                    setLoading(false);
                }
            } catch (error) {
                console.error('Failed to load category data:', error);
                setLoading(false);
            }
        };

        loadData();
    }, [cluster, locale, buildEnNameMap, cacheVersion]);

    // Get glossary definition for a category or subcategory name
    const getGlossaryDefinition = useCallback((name: string): GlossaryEntry | null => {
        // Try exact match first
        let entry = glossary.find(g =>
            g.terminology.toLowerCase() === name.toLowerCase()
        );
        if (entry) return entry;

        // Try partial match (glossary terminology contained in name or vice versa)
        entry = glossary.find(g => {
            const termLower = g.terminology.toLowerCase();
            const nameLower = name.toLowerCase();
            return nameLower.includes(termLower) || termLower.includes(nameLower);
        });
        return entry || null;
    }, [glossary]);

    // Toggle category expansion
    const toggleCategory = useCallback((categoryName: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryName)) {
                next.delete(categoryName);
            } else {
                next.add(categoryName);
            }
            return next;
        });
    }, []);

    return {
        categoryData,
        allCategoryData,
        weightsData,
        allWeightsData,
        glossary,
        expandedCategories,
        enNameMap,
        loading,
        getGlossaryDefinition,
        toggleCategory
    };
}
